import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import roundOneSvg from '../../../../assets/images/graphics/round-one.svg';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';
import { Tabs } from '../../../../ui';
import { getInterviewRounds } from '../../../../utils/api_service/JobOpenings';
import {
  Candidates,
  Configurations,
  Offer,
  Overview,
  Questions,
  Results,
} from './Containers';
import { InviteTemplate } from './Containers/InvitationTemplates/InviteTemplateCandidate';

export const Interviews = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const [interviewRounds, setInterviewRounds] = useState<any[]>([]);
  const [currentRound, setCurrentRound] = useState<string>('');
  const [isLoadingRounds, setIsLoadingRounds] = useState<boolean>(true);

  // Get interview tab from URL
  const getInterviewTabFromUrl = (): string => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('interviewTab');
    const validTabs = [
      'configuration',
      'overview',
      'invitationTemplate',
      'candidates',
      'questions',
      'results',
      'offer',
    ];
    return validTabs.includes(tab || '')
      ? tab || 'configuration'
      : 'configuration';
  };

  // Get round from URL
  const getRoundFromUrl = (): string | null => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('round');
  };

  const [activeTab, setActiveTab] = useState<string>(getInterviewTabFromUrl());

  const numberWords = [
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
  ];
  const getRoundLabel = (index: number) =>
    `Round ${numberWords[index] || index + 1}`;

  // Update URL when interview tab changes
  const updateActiveTab = (tab: string) => {
    setActiveTab(tab);
    const searchParams = new URLSearchParams();

    // Always ensure main tab is set correctly for Smart Interviews
    searchParams.set('tab', 'Smart Interviews');
    searchParams.set('interviewTab', tab);

    // Include current round in URL to preserve it on refresh
    if (currentRound) {
      searchParams.set('round', currentRound);
    }

    // Don't include any other sub-tab parameters
    // This prevents parameter pollution between different main tabs

    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  // Sync state with URL on location change
  useEffect(() => {
    const tabFromUrl = getInterviewTabFromUrl();
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search]);

  const fetchInterviewRounds = useCallback(async () => {
    setIsLoadingRounds(true);
    try {
      const response = await getInterviewRounds(id);
      const data = await response.json();
      // Sort rounds by their index/order
      const sortedRounds = data.sort((a: any, b: any) => {
        const aIndex = a.title?.match(/Round (\d+)/i)?.[1] || 0;
        const bIndex = b.title?.match(/Round (\d+)/i)?.[1] || 0;
        return Number(aIndex) - Number(bIndex);
      });

      setInterviewRounds(sortedRounds);

      // Check URL parameters for tab and round preservation
      const urlTab = getInterviewTabFromUrl();
      const urlRound = getRoundFromUrl();
      const hasUrlTab = new URLSearchParams(location.search).has(
        'interviewTab',
      );
      const hasUrlRound = new URLSearchParams(location.search).has('round');

      if (!hasUrlTab && !hasUrlRound) {
        // No URL parameters specified, set defaults based on data
        if (sortedRounds.length > 0) {
          setCurrentRound(getRoundLabel(0));
          updateActiveTab('overview');
        } else {
          setCurrentRound('');
          updateActiveTab('configuration');
        }
      } else {
        // URL parameters specified, respect user's choice
        if (hasUrlRound && urlRound) {
          // Validate that the URL round exists in the data
          const roundExists =
            sortedRounds.length > 0 &&
            sortedRounds.some(
              (_: any, idx: number) => getRoundLabel(idx) === urlRound,
            );

          if (roundExists) {
            setCurrentRound(urlRound);
          } else if (sortedRounds.length > 0) {
            // Fallback to first round if URL round doesn't exist
            setCurrentRound(getRoundLabel(0));
          } else {
            setCurrentRound('');
          }
        } else if (sortedRounds.length > 0) {
          // No round in URL but rounds exist, default to first round
          setCurrentRound(getRoundLabel(0));
        } else {
          setCurrentRound('');
        }

        // Keep the URL-based tab selection
        setActiveTab(urlTab);
      }
    } catch {
      setInterviewRounds([]);
    } finally {
      setIsLoadingRounds(false);
    }
  }, [id, location.search]);

  useEffect(() => {
    fetchInterviewRounds();
  }, [fetchInterviewRounds]);

  const buttonsList = useMemo(
    () =>
      interviewRounds.map((_, idx) => ({
        label: getRoundLabel(idx),
        svg: roundOneSvg,
      })),
    [interviewRounds],
  );

  const interviewRenderMap = useMemo(() => {
    const map: Record<string, React.ReactNode> = {};
    interviewRounds.forEach((round, idx) => {
      const label = getRoundLabel(idx);
      const isLastRound = idx === interviewRounds.length - 1;

      map[label] = (
        <div className='w-full mx-auto bg-white'>
          <Tabs
              tabs={[
                { value: 'overview', label: t(SI.tabs.overview) },
                { value: 'configuration', label: t(SI.tabs.configuration) },
                { value: 'invitationTemplate', label: t(SI.tabs.invitationTemplate) },
                { value: 'candidates', label: t(SI.tabs.candidates) },
                { value: 'results', label: t(SI.tabs.results) },
                ...(isLastRound ? [{ value: 'offer', label: t(SI.tabs.offer) }] : []),
              ]}
              activeTab={activeTab}
              onTabChange={updateActiveTab}
          />

          <div className='px-2 py-2 sm:px-3'>
            { !round.id ? (
              <div>Loading...</div>
            ) : (
              <>
                { activeTab === 'overview' && (
                  <React.Suspense
                      fallback={
                        <div className='flex items-center justify-center min-h-[200px]'>
                          <div className='flex flex-col items-center space-y-4'>
                            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-dark'></div>
                            <p className='text-primary-text'>{ t(SI.loading.overview) }</p>
                          </div>
                        </div>
                      }
                  >
                    <Overview interviewRoundId={round.id} />
                  </React.Suspense>
                ) }
                { activeTab === 'configuration' && (
                  <React.Suspense
                      fallback={
                        <div className='flex items-center justify-center min-h-[200px]'>
                          <div className='flex flex-col items-center space-y-4'>
                            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-dark'></div>
                            <p className='text-primary-text'>{ t(SI.loading.configuration) }</p>
                          </div>
                        </div>
                      }
                  >
                    <Configurations
                        jobOpeningId={id}
                        interviewId={round.id}
                        onRoundCreated={fetchInterviewRounds}
                    />
                  </React.Suspense>
                ) }
                { activeTab === 'invitationTemplate' && (
                  <React.Suspense
                      fallback={
                        <div className='flex items-center justify-center min-h-[200px]'>
                          <div className='flex flex-col items-center space-y-4'>
                            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-dark'></div>
                            <p className='text-primary-text'>{ t(SI.loading.invitationTemplate) }</p>
                          </div>
                        </div>
                      }
                  >
                    <InviteTemplate interviewId={round.id} />
                  </React.Suspense>
                ) }
                { activeTab === 'candidates' && (
                  <React.Suspense
                      fallback={
                        <div className='flex items-center justify-center min-h-[200px]'>
                          <div className='flex flex-col items-center space-y-4'>
                            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-dark'></div>
                            <p className='text-primary-text'>{ t(SI.loading.candidates) }</p>
                          </div>
                        </div>
                      }
                  >
                    <Candidates
                        interviewRoundID={round.id}
                        jobOpeningId={id}
                        allRounds={interviewRounds}
                        currentRoundIndex={interviewRounds.findIndex(
                          (r: any) => r.id === round.id,
                        )}
                    />
                  </React.Suspense>
                ) }
                { activeTab === 'questions' && <Questions /> }
                { activeTab === 'results' && (
                  <React.Suspense
                      fallback={
                        <div className='flex items-center justify-center min-h-[200px]'>
                          <div className='flex flex-col items-center space-y-4'>
                            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-dark'></div>
                            <p className='text-primary-text'>{ t(SI.loading.results) }</p>
                          </div>
                        </div>
                      }
                  >
                    <Results interviewRoundId={round.id} />
                  </React.Suspense>
                ) }
                { activeTab === 'offer' && idx === interviewRounds.length - 1 && (
                  <React.Suspense
                      fallback={
                        <div className='flex items-center justify-center min-h-[200px]'>
                          <div className='flex flex-col items-center space-y-4'>
                            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-dark'></div>
                            <p className='text-primary-text'>{ t(SI.loading.offer) }</p>
                          </div>
                        </div>
                      }
                  >
                    <Offer jobOpeningId={id} />
                  </React.Suspense>
                ) }
              </>
            ) }
          </div>
        </div>
      );
    });
    return map;
  }, [interviewRounds, activeTab, id]);

  const renderFormPage = () => {
    if (isLoadingRounds) {
      return (
        <div className='w-full mx-auto bg-white flex items-center justify-center min-h-[400px]'>
          <div className='flex flex-col items-center space-y-4'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-dark'></div>
            <p className='text-primary-text'>{ t(SI.loading.rounds) }</p>
          </div>
        </div>
      );
    }

    if (!currentRound) {
      return (
        <div className='w-full mx-auto bg-white'>
          <React.Suspense
              fallback={
                <div className='flex items-center justify-center min-h-[200px]'>
                  <div className='flex flex-col items-center space-y-4'>
                    <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-dark'></div>
                    <p className='text-primary-text'>{ t(SI.loading.configuration) }</p>
                  </div>
                </div>
              }
          >
            <Configurations
                jobOpeningId={id}
                interviewId=''
                onRoundCreated={fetchInterviewRounds}
            />
          </React.Suspense>
        </div>
      );
    }

    return (
      interviewRenderMap[currentRound] || (
        <div className='p-4'>
          <p>{ t(SI.noContent) }</p>
        </div>
      )
    );
  };

  // Helper function to check if a round button should be active
  const isRoundActive = (roundLabel: string) => {
    return currentRound === roundLabel && activeTab !== 'offer';
  };

  // Helper function to check if offer button should be active
  const isOfferActive = () => {
    return (
      currentRound === getRoundLabel(interviewRounds.length - 1) &&
      activeTab === 'offer'
    );
  };

  return (
    <div className='flex !bg-white mt-3 flex-col gap-2 lg:flex-row'>
      <div className='flex w-full gap-1.5 overflow-x-auto rounded-lg border border-slate-100 bg-slate-50/70 p-1.5 lg:sticky lg:top-4 lg:h-fit lg:w-16 lg:shrink-0 lg:flex-col lg:gap-2 lg:overflow-visible lg:rounded-2xl lg:py-3'>
        { /* New Round Button */ }
        <button
            className='group flex min-w-fit flex-row items-center gap-1.5 rounded-md px-1 py-1 transition-all duration-200 lg:flex-col lg:gap-0.5 lg:px-0'
            onClick={() => {
              setCurrentRound('');
              updateActiveTab('configuration');
            }}
        >
          <div className='flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 shadow-sm transition-all duration-200 group-hover:border-emerald-500 group-hover:text-emerald-600 group-hover:shadow-md group-hover:shadow-emerald-100 lg:h-9 lg:w-9 lg:rounded-xl'>
            <svg
                className='h-3.5 w-3.5 lg:h-4 lg:w-4'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
            >
              <line x1='12' y1='5' x2='12' y2='19'></line>
              <line x1='5' y1='12' x2='19' y2='12'></line>
            </svg>
          </div>
          <p className='text-[9px] font-bold uppercase leading-tight tracking-tight text-slate-400 transition-colors group-hover:text-emerald-600 lg:text-[7.5px]'>
            { t(SI.newRound) }
          </p>
        </button>

        <div className='hidden h-px bg-slate-200 mx-2 lg:block' />

        { /* Round Buttons */ }
        <div className='flex gap-1.5 lg:flex-col lg:gap-2'>
          { buttonsList.map(({ label }, idx) => {
            const isActive = isRoundActive(label);
            return (
              <button
                  key={label}
                  className='group flex min-w-fit flex-row items-center gap-1.5 rounded-md px-1 py-1 transition-all duration-200 lg:flex-col lg:gap-0.5 lg:px-0'
                  onClick={() => {
                    setCurrentRound(label);
                    // Update URL with new round and tab
                    const searchParams = new URLSearchParams();
                    searchParams.set('tab', 'Smart Interviews');
                    searchParams.set('interviewTab', 'overview');
                    searchParams.set('round', label);
                    const newUrl = `${
                      location.pathname
                    }?${searchParams.toString()}`;
                    window.history.pushState(null, '', newUrl);
                    setActiveTab('overview');
                  }}
              >
                <div
                    className={`flex h-8 w-8 items-center justify-center rounded-lg border text-xs font-bold transition-all duration-300 lg:h-9 lg:w-9 lg:rounded-xl ${
                      isActive
                        ? 'bg-emerald-600 border-emerald-600 text-white shadow-md shadow-emerald-100'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-emerald-400 hover:text-emerald-600 group-hover:shadow-md'
                    }`}
                >
                  { idx + 1 }
                </div>
                <p
                    className={`text-[9px] font-bold uppercase leading-tight tracking-tight transition-colors lg:text-[7.5px] ${
                      isActive
                        ? 'text-emerald-600'
                        : 'text-slate-400 group-hover:text-emerald-500'
                    }`}
                >
                  { label.split(' ')[1] }
                </p>
              </button>
            );
          }) }
        </div>

        { /* Offer Button */ }
        { interviewRounds.length > 0 && (
          <>
            <div className='hidden h-px bg-slate-200 mx-2 lg:block' />
            <button
                className='group flex min-w-fit flex-row items-center gap-1.5 rounded-md px-1 py-1 transition-all duration-200 lg:flex-col lg:gap-0.5 lg:px-0'
                onClick={() => {
                  const lastRoundLabel = getRoundLabel(
                    interviewRounds.length - 1,
                  );
                  setCurrentRound(lastRoundLabel);
                  // Update URL with round and offer tab
                  const searchParams = new URLSearchParams();
                  searchParams.set('tab', 'Smart Interviews');
                  searchParams.set('interviewTab', 'offer');
                  searchParams.set('round', lastRoundLabel);
                  const newUrl = `${
                    location.pathname
                  }?${searchParams.toString()}`;
                  window.history.pushState(null, '', newUrl);
                  setActiveTab('offer');
                }}
            >
              <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-all duration-300 lg:h-9 lg:w-9 lg:rounded-xl ${
                    isOfferActive()
                      ? 'bg-emerald-600 border-emerald-600 text-white shadow-md shadow-emerald-100'
                      : 'bg-white border-slate-200 text-slate-400 hover:border-emerald-400 hover:text-emerald-600 group-hover:shadow-md'
                  }`}
              >
                <svg
                    className='h-3.5 w-3.5 lg:h-4 lg:w-4'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                >
                  <circle cx='12' cy='12' r='10' />
                  <circle cx='12' cy='12' r='6' />
                  <circle cx='12' cy='12' r='2' />
                </svg>
              </div>
              <p
                  className={`text-[9px] font-bold uppercase leading-tight tracking-tight transition-colors lg:text-[7.5px] ${
                    isOfferActive()
                      ? 'text-emerald-600'
                      : 'text-slate-400 group-hover:text-emerald-500'
                  }`}
              >
                { t(SI.tabs.offer) }
              </p>
            </button>
          </>
        ) }
      </div>

      <div className='min-h-[360px] w-full min-w-0 overflow-hidden border border-slate-100 bg-slate-50/30 shadow-sm'>
        { renderFormPage() }
      </div>
    </div>
  );
};
